import { useState, useEffect, useRef } from "react";
import { api } from "../App";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, ChevronLeft, ChevronRight, Home, Calendar, CheckCircle, Menu, MessageCircle, Volume2, VolumeX, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import ChatBot from "../components/ChatBot";

const translations = {
  fr: {
    loading: "Chargement...",
    verse: "Verset",
    previous: "Jour précédent",
    next: "Jour suivant",
    backHome: "Retour à l'accueil",
    day: "Jour",
    chapters: "chapitres",
    verses: "versets",
    progress: "Progression",
    markCompleted: "Marquer comme lu",
    completed: "Complété",
    daysCompleted: "jours complétés",
    completedAndNext: "Jour complété ! Passage au jour suivant...",
    allCompleted: "Félicitations ! Vous avez terminé les 365 jours de lecture !",
    openChat: "Ouvrir l'assistant IA",
    playAudio: "Lire le chapitre",
    pauseAudio: "Pause",
    stopAudio: "Arrêter",
    audioPlaying: "Lecture en cours..."
  },
  en: {
    loading: "Loading...",
    verse: "Verse",
    previous: "Previous Day",
    next: "Next Day",
    backHome: "Back to Home",
    day: "Day",
    chapters: "chapters",
    verses: "verses",
    progress: "Progress",
    markCompleted: "Mark as Read",
    completed: "Completed",
    daysCompleted: "days completed",
    completedAndNext: "Day completed! Moving to next day...",
    allCompleted: "Congratulations! You have completed all 365 days of reading!",
    openChat: "Open AI assistant",
    playAudio: "Play chapter",
    pauseAudio: "Pause",
    stopAudio: "Stop",
    audioPlaying: "Playing..."
  },
  es: {
    loading: "Cargando...",
    verse: "Versículo",
    previous: "Día anterior",
    next: "Día siguiente",
    backHome: "Volver al inicio",
    day: "Día",
    chapters: "capítulos",
    verses: "versículos",
    progress: "Progreso",
    markCompleted: "Marcar como leído",
    completed: "Completado",
    daysCompleted: "días completados",
    completedAndNext: "¡Día completado! Pasando al día siguiente...",
    allCompleted: "¡Felicitaciones! ¡Has completado los 365 días de lectura!",
    openChat: "Abrir asistente IA",
    playAudio: "Reproducir capítulo",
    pauseAudio: "Pausa",
    stopAudio: "Detener",
    audioPlaying: "Reproduciendo..."
  }
};

const ReaderPage = ({ language }) => {
  const { day } = useParams();
  const navigate = useNavigate();
  const [readingData, setReadingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedDays, setCompletedDays] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const speechSynthesisRef = useRef(null);
  const t = translations[language];

  useEffect(() => {
    if (day) {
      fetchDayReading();
      checkIfCompleted();
    }
    
    // Load voices when component mounts
    const loadVoices = () => {
      window.speechSynthesis.getVoices();
    };
    
    // Load voices immediately
    loadVoices();
    
    // Also listen for when voices are loaded
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    
    // Cleanup audio on unmount
    return () => {
      stopAudio();
    };
  }, [day, language]);

  const checkIfCompleted = () => {
    const completed = JSON.parse(localStorage.getItem('completed_days') || '[]');
    setCompletedDays(completed);
    setIsCompleted(completed.includes(parseInt(day)));
  };

  const fetchDayReading = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/day-reading/${day}?language=${language}`);
      setReadingData(response.data);
    } catch (error) {
      console.error("Error fetching reading:", error);
      toast.error("Erreur lors du chargement de la lecture");
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (direction) => {
    const currentDay = parseInt(day);
    let newDay;
    
    if (direction === "next") {
      newDay = currentDay < 365 ? currentDay + 1 : 365;
    } else {
      newDay = currentDay > 1 ? currentDay - 1 : 1;
    }
    
    navigate(`/read/${newDay}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleMarkCompleted = () => {
    const currentDayNum = parseInt(day);
    const completed = JSON.parse(localStorage.getItem('completed_days') || '[]');
    
    if (!completed.includes(currentDayNum)) {
      completed.push(currentDayNum);
      completed.sort((a, b) => a - b);
      localStorage.setItem('completed_days', JSON.stringify(completed));
      setIsCompleted(true);
      setCompletedDays(completed);
      
      if (completed.length >= 365) {
        toast.success(t.allCompleted, { duration: 5000 });
        setTimeout(() => navigate('/'), 2000);
      } else {
        toast.success(t.completedAndNext);
        setTimeout(() => {
          let nextDay = currentDayNum + 1;
          while (nextDay <= 365 && completed.includes(nextDay)) {
            nextDay++;
          }
          if (nextDay <= 365) {
            navigate(`/read/${nextDay}`);
          } else {
            for (let i = 1; i <= 365; i++) {
              if (!completed.includes(i)) {
                navigate(`/read/${i}`);
                break;
              }
            }
          }
        }, 2000);
      }
    }
  };

  const playChapterAudio = (chapterIndex) => {
    if (!readingData || !readingData.chapters_data[chapterIndex]) return;
    
    stopAudio();
    
    const chapter = readingData.chapters_data[chapterIndex];
    
    // Get verse label based on language
    const verseLabel = language === 'fr' ? 'Verset' : language === 'en' ? 'Verse' : 'Versículo';
    
    const textToRead = chapter.verses.map(v => 
      `${verseLabel} ${v.verse_number}. ${v.text}`
    ).join(' ');
    
    const utterance = new SpeechSynthesisUtterance(textToRead);
    
    // Set basic properties
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    // Set language - this is the most important for voice selection
    if (language === 'fr') {
      utterance.lang = 'fr-FR';
    } else if (language === 'es') {
      utterance.lang = 'es-ES';
    } else {
      utterance.lang = 'en-US';
    }
    
    utterance.onstart = () => {
      console.log('Speech started for language:', utterance.lang);
    };
    
    utterance.onend = () => {
      console.log('Speech ended');
      setIsPlaying(false);
      setIsPaused(false);
      if (chapterIndex < readingData.chapters_data.length - 1) {
        setCurrentChapterIndex(chapterIndex + 1);
      } else {
        setCurrentChapterIndex(0);
      }
    };
    
    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsPlaying(false);
      setIsPaused(false);
      const errorMsg = language === 'fr' ? 'Erreur lors de la lecture audio' : 
                       language === 'en' ? 'Audio playback error' : 
                       'Error de reproducción de audio';
      toast.error(errorMsg);
    };
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    speechSynthesisRef.current = utterance;
    
    // Small delay to ensure cancellation is processed
    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
      setIsPlaying(true);
      setIsPaused(false);
      setCurrentChapterIndex(chapterIndex);
    }, 100);
  };

  const pauseAudio = () => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  const resumeAudio = () => {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  };

  const stopAudio = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setIsPaused(false);
  };

  const progressPercentage = (completedDays.length / 365) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-amber-900">{t.loading}</div>
      </div>
    );
  }

  if (!readingData) {
    return null;
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass-effect sticky top-0 z-50 border-b border-amber-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-amber-800" />
              <div>
                <h1 data-testid="day-title" className="text-xl sm:text-2xl font-bold text-amber-900 book-title">
                  {t.day} {readingData.day}
                </h1>
                <p className="text-sm text-amber-700">
                  {readingData.readings.map((r, idx) => (
                    <span key={idx}>
                      {r.book} {r.chapter}
                      {idx < readingData.readings.length - 1 ? " • " : ""}
                    </span>
                  ))}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="open-chatbot-button"
                onClick={() => setIsChatOpen(true)}
                variant="outline"
                className="border-amber-300"
                title={t.openChat}
              >
                <MessageCircle className="w-4 h-4" />
              </Button>
              <Button data-testid="back-home-button" onClick={() => navigate("/")} variant="outline" className="border-amber-300">
                <Home className="w-4 h-4 mr-2" />
                {t.backHome}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress and Stats */}
        <Card data-testid="reading-info-card" className="glass-effect border-amber-200 mb-6">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex justify-between text-sm text-amber-800 mb-2">
                <span>{t.progress}: {completedDays.length} / 365 {t.daysCompleted}</span>
                <span>{progressPercentage.toFixed(1)}%</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
              
              <div className="flex items-center justify-between pt-2">
                <div className="text-sm text-amber-800">
                  <span className="font-semibold">{readingData.readings.length}</span> {t.chapters} • 
                  <span className="font-semibold"> {readingData.total_verses}</span> {t.verses}
                </div>
                
                {!isCompleted ? (
                  <Button
                    data-testid="mark-completed-button"
                    onClick={handleMarkCompleted}
                    size="sm"
                    variant="outline"
                    className="border-green-600 text-green-700 hover:bg-green-50"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t.markCompleted}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">{t.completed}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Chapters */}
        {readingData.chapters_data.map((chapterData, chapterIdx) => (
          <Card
            key={chapterIdx}
            data-testid={`chapter-card-${chapterIdx}`}
            className="glass-effect border-amber-200 mb-6"
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-amber-900">
                  {chapterData.book} - {language === "fr" ? "Chapitre" : "Chapter"} {chapterData.chapter}
                </CardTitle>
                
                {/* Audio Controls */}
                <div className="flex items-center gap-2">
                  {isPlaying && currentChapterIndex === chapterIdx ? (
                    <>
                      {isPaused ? (
                        <Button
                          data-testid={`resume-audio-${chapterIdx}`}
                          onClick={resumeAudio}
                          size="sm"
                          variant="outline"
                          className="border-blue-600 text-blue-700"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {t.playAudio}
                        </Button>
                      ) : (
                        <Button
                          data-testid={`pause-audio-${chapterIdx}`}
                          onClick={pauseAudio}
                          size="sm"
                          variant="outline"
                          className="border-blue-600 text-blue-700"
                        >
                          <Pause className="w-4 h-4 mr-2" />
                          {t.pauseAudio}
                        </Button>
                      )}
                      <Button
                        data-testid={`stop-audio-${chapterIdx}`}
                        onClick={stopAudio}
                        size="sm"
                        variant="outline"
                        className="border-red-600 text-red-700"
                      >
                        <VolumeX className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      data-testid={`play-audio-${chapterIdx}`}
                      onClick={() => playChapterAudio(chapterIdx)}
                      size="sm"
                      className="btn-primary"
                    >
                      <Volume2 className="w-4 h-4 mr-2" />
                      {t.playAudio}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {chapterData.verses.map((verse) => (
                  <div key={verse.verse_number} data-testid={`verse-${verse.book}-${verse.chapter}-${verse.verse_number}`} className="fade-in">
                    <div className="flex gap-4">
                      <span className="text-amber-700 font-bold text-sm mt-1 flex-shrink-0">
                        {verse.verse_number}
                      </span>
                      <div className="flex-1">
                        <p className="verse-text">{verse.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Navigation */}
        <div className="flex justify-between items-center gap-4">
          <Button
            data-testid="previous-day-button"
            onClick={() => handleNavigation("previous")}
            variant="outline"
            className="border-amber-300"
            disabled={parseInt(day) <= 1}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {t.previous}
          </Button>
          <Button
            data-testid="next-day-button"
            onClick={() => handleNavigation("next")}
            className="btn-primary"
            disabled={parseInt(day) >= 365}
          >
            {t.next}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>

      {/* ChatBot Component */}
      <ChatBot
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        language={language}
        currentDay={parseInt(day)}
      />
    </div>
  );
};

export default ReaderPage;
